import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Warning as WarningIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

const SupervisionModal = ({ 
  open, 
  onClose, 
  onConfirm, 
  iniciativaId,
  supervisionStatus,
  loading = false
}) => {
  const [revisionLT, setRevisionLT] = useState(false);
  const [revisionDN, setRevisionDN] = useState(false);

  // Sincronizar estado de los checks cuando el modal se abre o cambia supervisionStatus
  React.useEffect(() => {
    if (open) {
      setRevisionLT(supervisionStatus?.revision_lt || false);
      setRevisionDN(supervisionStatus?.revision_dn || false);
    }
  }, [open, supervisionStatus]);

  const handleConfirm = () => {
    onConfirm({ revision_lt: revisionLT, revision_dn: revisionDN });
  };

  const isSupervised = revisionLT && revisionDN;
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {isSupervised ? (
            <CheckCircleIcon color="success" />
          ) : (
            <WarningIcon color="warning" />
          )}
          <Typography variant="h6">
            Supervisión de Propuesta
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {!supervisionStatus?.is_supervised && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta iniciativa no puede pasar al estado "Entregada" sin completar la supervisión.
            Ambos checks deben estar marcados.
          </Alert>
        )}

        {supervisionStatus?.is_supervised && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Esta iniciativa ha sido supervisada correctamente y puede pasar al estado "Entregada".
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Marca los siguientes checks para confirmar que la propuesta ha sido revisada:
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={revisionLT}
                onChange={(e) => setRevisionLT(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  Revisión Propuesta Líder Técnico
                </Typography>
                {supervisionStatus?.fecha_revision_lt && (
                  <Typography variant="caption" color="textSecondary">
                    Marcado el: {formatDate(supervisionStatus.fecha_revision_lt)}
                  </Typography>
                )}
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={revisionDN}
                onChange={(e) => setRevisionDN(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  Revisión Propuesta Desarrollo de Negocio
                </Typography>
                {supervisionStatus?.fecha_revision_dn && (
                  <Typography variant="caption" color="textSecondary">
                    Marcado el: {formatDate(supervisionStatus.fecha_revision_dn)}
                  </Typography>
                )}
              </Box>
            }
          />
        </Box>

        {!isSupervised && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Para continuar con el cambio de estado, marca ambos checks de supervisión.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          Cancelar
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="primary"
          disabled={!isSupervised || loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {isSupervised ? 'Confirmar y Cambiar Estado' : 'Guardar Supervisión'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupervisionModal;
